import { useState, useCallback, useEffect } from "react";
import { medicalClearanceService } from "../services/medicalClearanceService";
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useMedicalClearance = ({ showGeneralMessage, apiFilterPayload, user, userAccess }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [requirements, setRequirements] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isProcessor = userAccess?.isMedicalClearanceProcessor && !userAccess?.isMedicalClearanceManager;
      const res = await medicalClearanceService.getPreview(apiFilterPayload, isProcessor);
      setData(res.data || []);
      setOriginalData(res.data || []);
    } catch (err) {
      showGeneralMessage("Failed to load records", false);
    } finally {
      setLoading(false);
    }
  }, [apiFilterPayload, userAccess, showGeneralMessage]);

  const fetchRequirements = useCallback(async () => {
    try {
      const res = await medicalClearanceService.getActiveRequirements();
      setRequirements(res.data || []);
    } catch (err) {
      showGeneralMessage("Failed to load clinic requirements", false);
    }
  }, [showGeneralMessage]);

  const getAssignmentDetails = useCallback(async (id) => {
    try {
      const res = await medicalClearanceService.getClearanceDetails(id);
      return res.data;
    } catch (err) {
      showGeneralMessage("Failed to load assignment details", false);
      return null;
    }
  }, [showGeneralMessage]);

  const saveBatchDetails = async (headerId, detailsList) => {
    setLoading(true);
    try {
      const payload = detailsList.map(d => ({
        id: d.id,
        isCompleted: d.isCompleted,
        bp: d.bp,
        remarks: d.remarks
      }));

      await medicalClearanceService.updateBatchDetails(headerId, payload);
      showGeneralMessage("Progress saved successfully!", true);
      return true;
    } catch (err) {
      showGeneralMessage("Failed to save progress", false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const finalizeClearance = async (headerId) => {
    setLoading(true);
    try {
      await medicalClearanceService.finalizeClearance(headerId, user.id);
      showGeneralMessage("Medical Clearance marked as COMPLETE!", true);
      fetchData(); // Refresh the main table
      return true;
    } catch (err) {
      showGeneralMessage("Failed to finalize clearance", false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (value) => {
    if (!value) return setData(originalData);
    const lower = value.toLowerCase();
    setData(originalData.filter(x =>
      x.employeeFullName?.toLowerCase().includes(lower) ||
      x.transactionNumber?.toLowerCase().includes(lower)
    ));
  };

  const createAssignment = async (form) => {
    setLoading(true);
    try {
      const payload = {
        employeePersonalInformationId: form.employeePersonalInformationId,
        requirementIds: form.requirementIds,
        remarks: form.remarks,
        createdBy: user?.id
      };
      await medicalClearanceService.createAssignment(payload);
      showGeneralMessage("Draft Assignment created!", true);
      fetchData();
      return true;
    } catch (err) { return false; }
    finally { setLoading(false); }
  };

  const updateAssignment = async (id, form) => {
    setLoading(true);
    try {
      const payload = {
        employeePersonalInformationId: form.employeePersonalInformationId,
        requirementIds: form.requirementIds,
        remarks: form.remarks,
        createdBy: user?.id
      };
      await medicalClearanceService.updateAssignment(id, payload);
      showGeneralMessage("Assignment updated successfully!", true);
      fetchData();
      return true;
    } catch (err) { return false; }
    finally { setLoading(false); }
  };

  const releaseAssignment = async (id) => {
    setLoading(true);
    try {
      await medicalClearanceService.releaseAssignment(id, user.id);
      showGeneralMessage("Released to Clinic!", true);
      fetchData();
    } catch (err) { showGeneralMessage("Failed to release", false); }
    finally { setLoading(false); }
  };

  const deleteAssignment = async (id) => {
    setLoading(true);
    try {
      await medicalClearanceService.deleteAssignment(id);
      showGeneralMessage("Assignment deleted.", true);
      fetchData();
    } catch (err) { showGeneralMessage("Failed to delete", false); }
    finally { setLoading(false); }
  };

  return {
    loading,
    data,
    requirements,
    handleSearch,
    createAssignment,
    updateAssignment,
    releaseAssignment,
    deleteAssignment,
    fetchRequirements,
    getAssignmentDetails,
    // updateRequirementStatus, // ADDED
    saveBatchDetails,
    finalizeClearance, // ADDED
    refresh: fetchData
  };
};